import { useQuery } from 'react-query';
import {
  TCheckSamePatientResponse,
  TRegisterChartParams,
} from 'types/apis/chart/studentEvaluationChart';
import { TCustomAxiosError, isTCustomAxiosError } from 'apis/config/axios';
import { getCheckSamePatient } from 'apis/chart/studentEvaluationChart';
import useModal from 'hooks/useModal';

export type TChangePatient = {
  patientId: string;
  courseInfoId: number;
  chartTypeNum: number;
  registeredChartId: number;
  isError: boolean;
  data: TCheckSamePatientResponse;
};

type TProps = {
  courseInfoId: number | undefined;
  chartRound: number | undefined;
  registeredChartId: number;
  chartTypeNum: number | undefined;
  patientId: string | undefined;
  submit: boolean;
  setSubmit: (value: boolean) => void;
  goToChart: (evaluationChartId: number, evaluationRound: number) => void;
  createChart: (data: TRegisterChartParams) => void;
  onChangePatient?: ({
    patientId,
    courseInfoId,
    chartTypeNum,
    registeredChartId,
    isError,
    data,
  }: TChangePatient) => void;
};

const useCheckChart = ({
  courseInfoId,
  registeredChartId,
  chartTypeNum,
  patientId,
  submit,
  setSubmit,
  goToChart,
  createChart,
  onChangePatient,
}: TProps) => {
  const { openModal } = useModal();

  const onError = (error?: TCustomAxiosError) => {
    if (error && isTCustomAxiosError(error)) {
      if (error.code === 819) {
        openModal({
          title: '대상자 변경이 불가합니다.',
          text: '1차 작성이 마감되어 대상자 변경이 불가합니다.',
        });
      }
    } else {
      openModal({
        title: '차트 생성 실패',
        text: '관리자에게 문의해 주세요.',
      });
    }
  };

  const changePatient = (data: TCheckSamePatientResponse) => {
    if (!data || !patientId || !courseInfoId || !chartTypeNum) {
      onError();
      return;
    }
    onChangePatient &&
      onChangePatient({
        patientId,
        courseInfoId,
        chartTypeNum,
        registeredChartId,
        isError: false,
        data,
      });
  };

  return useQuery(
    [
      'getCheckSamePatient',
      patientId,
      courseInfoId,
      registeredChartId,
      chartTypeNum,
    ],
    () =>
      getCheckSamePatient({
        patientId: Number(patientId),
        courseInfoId: Number(courseInfoId),
        registeredChartId,
        chartTypeNum: Number(chartTypeNum),
      }),
    {
      enabled: submit && !!patientId && !!courseInfoId && !!registeredChartId,
      onSuccess: ({ data }) => {
        setSubmit(false);
        if (!data || (!data.isSamePatient && !data.evaluationChartIdList))
          throw new Error('차트 생성 실패');
        if (data.isSamePatient) {
          if (data.createdEvaluationChartId) {
            goToChart(
              data.createdEvaluationChartId,
              data.evaluationChartRound || 0
            );
          } else {
            createChart({
              isSamePatient: data.isSamePatient,
              registerRequest: {
                patientId: Number(patientId),
                courseInfoId: Number(courseInfoId),
                registeredChartId,
                chartTypeNum: Number(chartTypeNum),
              },
            });
          }
        } else {
          changePatient(data);
        }
      },
      onError,
    }
  );
};

export default useCheckChart;
